import {
    buildLogger,
    Logger,
    LoggingService,
    timeUtils,
} from '@instinet-analytics/core-lib';
import {
    BarDataSubscriptionDataItem,
    BarDataSubscriptionRequestPayload,
    HistoricalBarDataItem,
    HistoricalBarDataRequestPayload,
    ReferenceDataItem,
    RequestReferenceDataPayload,
    SidecarApi,
    SidecarSingleResponseRequest,
    SidecarStreamRequest,
} from '@instinet-bcng/sidecar-client';
import { SidecarRequestManager } from './SidecarRequestManager';
import {
    BloombergMarketDataRequest,
    MarketDataRequestBarRequest,
} from 'marketData/MarketDataRequest';
import {
    BarDataReceivedHandler,
    RefDataReceivedHandler,
} from 'marketData/ExternalMarketDataStrategy';

import { v4 as uuid } from 'uuid';
import {
    barDataSubscriptionItemToBarData,
    historicalBarItemToBarData,
} from 'app/openFin/sidecar/requests/mapping';
import {
    addRequest,
    recordMessage,
} from 'app/glue/bloomberg/store/bloombergSlice';
import { ActionsDispatcher } from 'store/ActionsDispatcher';

const { roundToDays } = timeUtils;

export class SidecarBarRequestManager extends SidecarRequestManager<
    MarketDataRequestBarRequest & BloombergMarketDataRequest
> {
    private logger: Logger;

    private readonly onBarData: BarDataReceivedHandler;
    private readonly onRefData: RefDataReceivedHandler;

    private refDataApiRequest:
        | SidecarSingleResponseRequest<
              RequestReferenceDataPayload,
              ReferenceDataItem
          >
        | undefined;
    private refDataApiRequestUid = '';

    private historicalBarDataApiRequest:
        | SidecarSingleResponseRequest<
              HistoricalBarDataRequestPayload,
              HistoricalBarDataItem
          >
        | undefined;
    private historicalBarDataApiRequestUid = '';
    private barDataApiRequest:
        | SidecarStreamRequest<
              BarDataSubscriptionRequestPayload,
              BarDataSubscriptionDataItem
          >
        | undefined;
    private barDataApiRequestUid = '';
    private referenceDataResponsePayload: ReferenceDataItem | undefined;

    public constructor(options: {
        request: MarketDataRequestBarRequest & BloombergMarketDataRequest;
        api: SidecarApi;
        loggingService: LoggingService;
        dispatcher: ActionsDispatcher;
        onBarData: BarDataReceivedHandler;
        onRefData: RefDataReceivedHandler;
    }) {
        super({
            api: options.api,
            request: options.request,
            dispatcher: options.dispatcher,
        });

        this.logger = buildLogger(
            'SidecarBarRequestManager',
            options.loggingService
        );
        this.onBarData = options.onBarData;
        this.onRefData = options.onRefData;
    }

    public override async subscribe() {
        if (this.haveRefDataResponseToday()) {
            this.logger.debug('Using previous ref data response', {
                responseTimestamp: this.refDataApiRequest?.responseTimestamp,
            });
            this.getHistoricalBars();
        } else {
            this.refDataApiRequestUid = uuid();
            const payload = {
                symbol: this.request.bbSymbol,
                correlationId: this.refDataApiRequestUid,
            };
            this.logger.debug('Requesting reference data', payload);
            this.refDataApiRequest = await this.api.requestReferenceData({
                payload,
                onData: this.handleRefReceived.bind(this),
                onError: (err) => {
                    this.logger.critical(
                        'Error from requestReferenceData',
                        err
                    );
                },
            });

            this.dispatch(
                addRequest({
                    uid: this.refDataApiRequestUid,
                    requestType: 'requestReferenceData',
                    request: this.request,
                    args: payload,
                })
            );
        }

        this.barDataApiRequestUid = uuid();
        const barPayload = {
            symbol: this.request.bbSymbol,
            barSize: this.request.barSize,
            correlationId: this.barDataApiRequestUid,
        };

        this.logger.debug('Subscribing to bar data', barPayload);

        this.barDataApiRequest = await this.api.subscribeToBarData({
            payload: barPayload,
            onData: this.handleBarDataReceived.bind(this),
            onError: (err) => {
                this.logger.critical(
                    'Error from handleHistoricalBarsReceived',
                    err
                );
            },
        });

        this.dispatch(
            addRequest({
                uid: this.barDataApiRequestUid,
                requestType: 'subscribeToBarData',
                request: this.request,
                args: barPayload,
            })
        );
    }

    public override async unsubscribe() {
        if (this.historicalBarDataApiRequest) {
            await this.historicalBarDataApiRequest.unsubscribe();
        }
        if (this.barDataApiRequest) {
            await this.barDataApiRequest.unsubscribe();
        }
    }

    private handleRefReceived(item: ReferenceDataItem) {
        this.referenceDataResponsePayload = item;
        const { symbol, end, start, volumeAvg20Day, prevClose } = item;

        const startDate = new Date(start);
        this.onRefData({
            bbSymbol: symbol,
            pnc: prevClose,
            averageVolume: volumeAvg20Day,
            startTradingHoursTimestamp: startDate.getTime(),
            endTradingHoursTimestamp: new Date(end).getTime(),
        });

        this.dispatch(
            recordMessage({
                uid: this.refDataApiRequestUid,
                requestType: 'requestReferenceData',
                eventType: 'sidecarData',
                response: item,
            })
        );

        this.getHistoricalBars();
    }

    private getHistoricalBars() {
        if (!this.referenceDataResponsePayload) {
            return;
        }
        const { start } = this.referenceDataResponsePayload;
        const startDate = new Date(start);

        if (startDate.getTime() < new Date().getTime()) {
            let start = startDate;

            if (this.historicalBarDataApiRequest?.responseTimestamp) {
                if (
                    this.historicalBarDataApiRequest.responseTimestamp >
                    start.getTime()
                ) {
                    // Start from time of previous historical bars response (less 5 mins) if there is one...
                    start = new Date(
                        this.historicalBarDataApiRequest.responseTimestamp -
                            5 * 60000
                    );
                }
            }

            (async () => {
                this.historicalBarDataApiRequestUid = uuid();
                const payload = {
                    symbol: this.request.bbSymbol,
                    barSize: this.request.barSize,
                    correlationId: this.historicalBarDataApiRequestUid,
                    start: start.toISOString(),
                    end: new Date().toISOString(),
                };

                this.logger.debug(
                    `Fetching historical bars from ${start.toISOString()}`,
                    { payload }
                );
                this.historicalBarDataApiRequest =
                    await this.api.requestHistoricalBarData({
                        payload,
                        onData: this.handleHistoricalBarsReceived.bind(this),
                        onError: (err) => {
                            this.logger.critical(
                                'Error from requestHistoricalBarData',
                                err
                            );
                        },
                    });
                this.dispatch(
                    addRequest({
                        request: this.request,
                        uid: this.historicalBarDataApiRequestUid,
                        requestType: 'requestHistoricalBarData',
                        args: payload,
                    })
                );
            })();
        }
    }

    private handleHistoricalBarsReceived(data: HistoricalBarDataItem) {
        this.onBarData(
            data.bars.map((bar) =>
                historicalBarItemToBarData(
                    this.request.bbSymbol,
                    this.request.barSize,
                    bar
                )
            )
        );

        this.dispatch(
            recordMessage({
                uid: this.historicalBarDataApiRequestUid,
                requestType: 'requestHistoricalBarData',
                eventType: 'sidecarData',
                response: data,
            })
        );
    }

    private handleBarDataReceived(data: BarDataSubscriptionDataItem) {
        this.onBarData([
            barDataSubscriptionItemToBarData(
                this.request.bbSymbol,
                data,
                this.request.barSize
            ),
        ]);

        this.dispatch(
            recordMessage({
                uid: this.barDataApiRequestUid,
                requestType: 'subscribeToBarData',
                eventType: 'sidecarData',
                response: data,
            })
        );
    }

    private haveRefDataResponseToday() {
        if (!this.refDataApiRequest?.responseTimestamp) {
            return;
        }
        const today = new Date(roundToDays(new Date().getTime()));
        return this.refDataApiRequest.responseTimestamp >= today.getTime();
    }
}
